import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DocumentGrid from "./DocumentGrid";
import { motion } from "framer-motion";
import { ScaleIcon, XMarkIcon, PlusIcon, FunnelIcon, MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import UploadDocumentModal from "./UploadDocumentModal";

// Définition dynamique de l'URL backend
const BACKEND_URL =
  window.location.hostname === "localhost"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PROD;

export default function Legal() {
  const [documents, setDocuments] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newDocument, setNewDocument] = useState({ nom: "", lien: "", type: "pdf", fichier: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  const navigate = useNavigate();

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [searchTerm, filterType, documents]);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ressources`);
      const legalDocs = res.data.filter((doc) => doc.categorie?.toLowerCase() === "legal");
      setDocuments(legalDocs);
      setFilteredDocs(legalDocs);
    } catch (err) {
      console.error("❌ Erreur lors du chargement des documents :", err);
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = () => {
    let filtered = documents.filter((doc) =>
      doc.nom.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filterType !== "all") {
      filtered = filtered.filter((doc) => doc.type === filterType);
    }

    setFilteredDocs(filtered);
  };

  const handleAddDocument = async () => {
    if (!newDocument.nom || (!newDocument.lien && !newDocument.fichier)) {
      alert("Veuillez sélectionner un fichier ou renseigner un lien.");
      return;
    }

    try {
      let res;

      if (newDocument.fichier) {
        const formData = new FormData();
        formData.append("nom", newDocument.nom);
        formData.append("type", newDocument.type);
        formData.append("categorie", "legal");
        formData.append("file", newDocument.fichier);

        res = await axios.post(`${BACKEND_URL}/api/ressources/upload`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await axios.post(`${BACKEND_URL}/api/ressources`, {
          ...newDocument,
          categorie: "Legal"
        });
      }

      setDocuments([...documents, res.data]);
      setIsModalOpen(false);
      setNewDocument({ nom: "", lien: "", type: "pdf", fichier: null });
    } catch (err) {
      console.error("❌ Erreur lors de l'ajout du document :", err);
      alert("Erreur lors de l'ajout du document.");
    }
  };

  return (
    <div className="flex items-center justify-center h-[80vh] px-6 lg:px-20 py-10">
      <div className="relative w-screen bg-white rounded-3xl shadow-2xl overflow-hidden h-[75vh] mt-20">

        <div className="flex items-center justify-between p-6 border-b bg-gray-100">
          <div className="flex items-center space-x-4">
            <motion.div
              className="p-3 bg-white shadow-md rounded-full"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <ScaleIcon className="w-10 h-10 text-red-500" />
            </motion.div>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Documents Légaux
            </h1>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center px-3 py-2 bg-red-500 text-white rounded-lg shadow-md hover:bg-red-600 transition"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Ajouter
            </button>

            <button
              onClick={() => navigate("/documents")}
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full transition-all"
            >
              <XMarkIcon className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div className="relative w-2/3">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un document..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button
            onClick={() => setFilterType(filterType === "all" ? "pdf" : filterType === "pdf" ? "image" : "all")}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg shadow-md"
          >
            <FunnelIcon className="w-5 h-5 text-gray-600" />
            <span className="text-gray-700">{filterType === "all" ? "Tous" : filterType.toUpperCase()}</span>
          </button>
        </div>

        <div className="p-6 overflow-y-auto h-[55vh]">
          {loading ? (
            <div className="text-center text-gray-500 text-lg animate-pulse">
              Chargement des documents...
            </div>
          ) : filteredDocs.length > 0 ? (
            <DocumentGrid categorie="legal" />
          ) : (
            <p className="text-gray-500 text-center">Aucun document disponible.</p>
          )}
        </div>
      </div>

      <UploadDocumentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddDocument}
        documentData={newDocument}
        setDocumentData={setNewDocument}
      />
    </div>
  );
}